import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { cn } from '@costar-ui/cosmos';
import { IconArrowCaretDown } from '@costar-ui/icons/csg/IconArrowCaretDown';
import React, { useMemo, useCallback } from 'react';
import { composeRenderProps } from 'react-aria-components';
import { useListData } from 'react-stately';
import _ComboBox from './aria/combo-box.esm.js';
import { useComponentTheme } from '../../theme/theme.context.esm.js';
import Button from '../button/button.esm.js';
import _ListBox from '../collections/aria/list-box/list-box.esm.js';
import ListBoxItem from '../collections/list-box-item.esm.js';
import Description from '../field/description.esm.js';
import FieldError from '../field/field-error/field-error.esm.js';
import { FieldGroup } from '../field/field-group/field-group.esm.js';
import Input from '../field/input/input.esm.js';
import Label from '../field/label/label.esm.js';
import { PopoverCore } from '../popover/popover-core.esm.js';
import Text from '../text/text.esm.js';

var _excluded = ["className", "defaultSelectedKey", "label", "description", "errorMessage", "items", "layout", "onSelectionChange", "screenReaderOnly", "size", "testId"];
function ComboBox(_ref) {
  var _className = _ref.className,
    defaultSelectedKey = _ref.defaultSelectedKey,
    label = _ref.label,
    description = _ref.description,
    errorMessage = _ref.errorMessage,
    items = _ref.items,
    _ref$layout = _ref.layout,
    layout = _ref$layout === void 0 ? 'relative' : _ref$layout,
    onSelectionChange = _ref.onSelectionChange,
    screenReaderOnly = _ref.screenReaderOnly,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'std' : _ref$size,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? 'tw-combo-box' : _ref$testId,
    props = _objectWithoutProperties(_ref, _excluded);
  var theme = useComponentTheme('ComboBox');
  // note: this pattern of resolving the tailwind classes only works if it's
  // has no direct dependency on any state of renderProps from react-aria context.
  // if there are any style that are dependent on the state, we will need to use data-xxx attribute
  // that react-aria provide to manage the style change.
  var _useMemo = useMemo(function () {
      var _theme = theme({
          size: size,
          layout: layout,
          screenReaderOnly: screenReaderOnly
        }),
        base = _theme.base,
        label = _theme.label,
        button = _theme.button,
        subText = _theme.subText,
        listBox = _theme.listBox;
      return {
        baseClasses: base(),
        labelClasses: label(),
        buttonClasses: button(),
        subTextClasses: subText(),
        listBoxClasses: listBox()
      };
    }, [layout, size, screenReaderOnly, theme]),
    baseClasses = _useMemo.baseClasses,
    labelClasses = _useMemo.labelClasses,
    buttonClasses = _useMemo.buttonClasses,
    subTextClasses = _useMemo.subTextClasses,
    listBoxClasses = _useMemo.listBoxClasses;
  var list = useListData({
    initialItems: items,
    initialSelectedKeys: [defaultSelectedKey],
    getKey: function getKey(item) {
      return item.id;
    }
  });
  var classes = useMemo(function () {
    return composeRenderProps(cn(baseClasses, _className), function (className) {
      return "csg-tw-combo-box ".concat(className);
    });
  }, [baseClasses, _className]);
  var handleOnSelectionChange = useCallback(function (key) {
    onSelectionChange(key ? list.getItem(key) : null);
  }, [list, onSelectionChange]);
  return /*#__PURE__*/React.createElement(_ComboBox, _extends({}, props, {
    "data-testid": testId,
    className: classes,
    onSelectionChange: handleOnSelectionChange
  }), label && /*#__PURE__*/React.createElement(Label, {
    className: labelClasses,
    ellipsis: "1"
  }, label), /*#__PURE__*/React.createElement(FieldGroup, {
    size: size
  }, /*#__PURE__*/React.createElement(Input, {
    testId: "".concat(testId, "-input"),
    paddingX: "sm",
    ellipsis: "1",
    canShrink: true
  }), /*#__PURE__*/React.createElement(Button, {
    testId: "".concat(testId, "-button"),
    variant: "ghost-secondary",
    className: buttonClasses
  }, /*#__PURE__*/React.createElement(IconArrowCaretDown, {
    "aria-hidden": true,
    size: "1em"
  }))), description && /*#__PURE__*/React.createElement(Description, {
    className: subTextClasses,
    ellipsis: "1"
  }, description), /*#__PURE__*/React.createElement(FieldError, {
    className: subTextClasses
  }, errorMessage), /*#__PURE__*/React.createElement(PopoverCore, {
    className: "w-[--trigger-width]",
    trigger: "Dropdown",
    offset: 14
  }, /*#__PURE__*/React.createElement(_ListBox, {
    items: list.items,
    className: listBoxClasses,
    selectedKeys: list.selectedKeys
  }, function (item) {
    return /*#__PURE__*/React.createElement(ListBoxItem, {
      id: item.id,
      className: cn({
        // note: these size evaluation only occurs at the initialization,
        // changes will have no effect after initialization
        'px-2.5 py-1.5': size === 'std',
        'px-2 py-1': size === 'sm',
        'px-1.5 py-1': size === 'xxs'
      }),
      "data-testid": "".concat(testId, "-option-").concat(item.id)
    }, /*#__PURE__*/React.createElement(Text, {
      slot: "label",
      ellipsis: "1"
    }, item.label));
  })));
}

export { ComboBox as default };
//# sourceMappingURL=combo-box.esm.js.map
